import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { AlertCircle, Check, Eye, Loader2, UploadCloud, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { ButtonTip } from "@/components/ui/button-tip";
import type { DataSlot, PublicFileView, PublicSlotInfo } from "../OnboardingAPI";
import { Collapse, CollapsePresence, EASE, EASE_ARRAY, usePrefersReducedMotion } from "./calm";
import { assetHints, peekCsv, type CsvPeek } from "./csvPeek";
import FileTypeIcon from "./FileTypeIcon";
import FilePreviewDialog, { canPreview, type PreviewApi } from "./FilePreviewDialog";
import { readDrop, dragHasFiles } from "./dropFiles";
import { formatBytes } from "./onboardingModel";

/**
 * A file the browser holds. It is created when a file is picked and lives until it is dismissed or
 * removed - also after the upload has finished (`done`, with the stored file's id), so its row keeps
 * its identity and does not remount when the server has stored the file.
 */
export interface UploadItem {
  id: string;
  slot: DataSlot;
  file: File;
  status: "waiting" | "uploading" | "error" | "done";
  /** 0..1 */
  progress: number;
  errorCode?: string;
  /** Set once the server has stored the file. */
  fileId?: string;
  /** Seconds a 429 asked to wait (its `Retry-After`), when it sent one. */
  retryAfterSeconds?: number;
  /** Waiting for an automatic retry after a 429. */
  retrying?: boolean;
}

interface SlotUploaderProps {
  slot: PublicSlotInfo;
  title: string;
  description: string;
  stored: PublicFileView[];
  items: UploadItem[];
  /** False once the lead is submitted: the files stay visible, nothing can be added or removed. */
  canEdit: boolean;
  /** A short message about this slot (e.g. a failed removal, or files of a folder that were left out). */
  notice?: string | null;
  errorText: (code: string, slot: PublicSlotInfo, retryAfterSeconds?: number) => string;
  onPick: (files: File[]) => void;
  onRemove: (file: PublicFileView) => void;
  onRetry: (item: UploadItem) => void;
  onDismiss: (item: UploadItem) => void;
  onDismissNotice?: () => void;
  /** Files are being dragged over the page: the drop zone rises and its frame draws itself. */
  dragging?: boolean;
  /** The smaller form for the page that collects several optional slots; it shows its own title. */
  compact?: boolean;
  /** Friendly hints for an asset list (no id column, generic names). */
  hints?: boolean;
  /** Files left out of a dropped folder (not an allowed type): the page shows it as a notice. */
  onSkipped?: (count: number) => void;
  /** The look into a stored file ("Ansehen"); without it there is no such button. */
  preview?: PreviewApi;
}

const RETRYABLE = new Set(["network", "unknown", "rate_limited"]);

interface Row {
  key: string;
  name: string;
  size: number;
  state: "stored" | "waiting" | "uploading" | "error";
  progress: number;
  errorCode?: string;
  retryAfterSeconds?: number;
  retrying?: boolean;
  file?: PublicFileView;
  item?: UploadItem;
  /** The File the browser holds, when this session picked it: the small look inside needs it. */
  local?: File;
}

/** Stored files first (reloaded ones), then this session's files in the order they were picked. */
function buildRows(stored: PublicFileView[], items: UploadItem[]): Row[] {
  const linked = new Set(items.map((item) => item.fileId).filter(Boolean) as string[]);
  const rows: Row[] = stored
    .filter((file) => !linked.has(file.id))
    .map((file) => ({ key: file.id, name: file.originalName, size: file.size, state: "stored", progress: 1, file }));
  items.forEach((item) => {
    if (item.status === "done") {
      const file = stored.find((candidate) => candidate.id === item.fileId);
      if (!file) return;
      rows.push({ key: item.id, name: file.originalName, size: file.size, state: "stored", progress: 1, file, item, local: item.file });
    } else {
      rows.push({
        key: item.id,
        name: item.file.name,
        size: item.file.size,
        state: item.status,
        progress: item.progress,
        errorCode: item.errorCode,
        retryAfterSeconds: item.retryAfterSeconds,
        retrying: item.retrying,
        item,
        local: item.file,
      });
    }
  });
  return rows;
}

const peeks = new WeakMap<File, Promise<CsvPeek | null>>();

/** The small look inside a CSV/TXT the browser holds: computed once per file, locally, from its first 64 KB. */
function useCsvPeek(file: File | undefined): CsvPeek | null {
  const [peek, setPeek] = useState<CsvPeek | null>(null);
  useEffect(() => {
    if (!file) return;
    let cancelled = false;
    let promise = peeks.get(file);
    if (!promise) {
      promise = peekCsv(file);
      peeks.set(file, promise);
    }
    void promise.then((result) => {
      if (!cancelled) setPeek(result);
    });
    return () => {
      cancelled = true;
    };
  }, [file]);
  return peek;
}

function StateIcon({ state }: { state: Row["state"] }) {
  const layer = (active: boolean) =>
    cn("absolute inset-0 h-4 w-4 transition-[opacity,transform] duration-300", active ? "scale-100 opacity-100" : "scale-75 opacity-0");
  return (
    <span className="relative block h-4 w-4 shrink-0" aria-hidden="true">
      <Check className={cn(layer(state === "stored"), "text-success", state === "stored" && "onb-pop")} />
      <Loader2 className={cn(layer(state === "waiting" || state === "uploading"), "animate-spin text-muted-foreground motion-reduce:animate-none")} />
      <AlertCircle className={cn(layer(state === "error"), "text-muted-foreground")} />
    </span>
  );
}

function FileCard({
  row,
  canEdit,
  slot,
  locale,
  hints,
  errorText,
  onRemove,
  onRetry,
  onDismiss,
  onPreview,
}: {
  row: Row;
  canEdit: boolean;
  slot: PublicSlotInfo;
  locale: string;
  hints: boolean;
  errorText: SlotUploaderProps["errorText"];
  onRemove: SlotUploaderProps["onRemove"];
  onRetry: SlotUploaderProps["onRetry"];
  onDismiss: SlotUploaderProps["onDismiss"];
  onPreview?: (file: PublicFileView, opener: HTMLElement) => void;
}) {
  const { t } = useTranslation();
  const lastError = useRef("");
  if (row.state === "error") lastError.current = errorText(row.errorCode ?? "unknown", slot, row.retryAfterSeconds);
  const busy = row.state === "waiting" || row.state === "uploading";
  const percent = Math.round(row.progress * 100);
  const peek = useCsvPeek(row.state === "stored" || row.state === "uploading" ? row.local : undefined);
  const nudge = peek && hints ? assetHints(peek) : null;
  const columnList = peek ? peek.columns.slice(0, 6).join(", ") + (peek.columns.length > 6 ? ` ${t("onboardingPage.peek.more", { count: peek.columns.length - 6 })}` : "") : "";

  return (
    <div
      className={cn(
        "onb-hover-draw rounded-xl border bg-card px-3 py-2.5 text-sm transition-colors duration-300",
        row.state === "error" ? "border-border bg-muted/40" : "border-border",
      )}
      data-state={row.state}
    >
      <div className="flex items-center gap-3">
        <FileTypeIcon name={row.name} className="text-muted-foreground" />
        <span className="min-w-0 flex-1 font-medium [overflow-wrap:anywhere]" title={row.name}>
          {row.name}
        </span>
        <span className="shrink-0 text-xs text-muted-foreground">{formatBytes(row.size, locale)}</span>
        <StateIcon state={row.state} />
        {canEdit && onPreview && row.state === "stored" && row.file && canPreview(row.name) && (
          <ButtonTip label={t("onboardingPage.tips.viewFile")}>
<button
            type="button"
            onClick={(event) => onPreview(row.file!, event.currentTarget)}
            aria-label={t("onboardingPage.preview.view", { name: row.name })}
            data-testid={`preview-open-${row.file.id}`}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-sm:h-11 max-sm:w-11"
          >
            <Eye className="h-4 w-4" aria-hidden="true" />
          </button>
          </ButtonTip>
        )}
        {canEdit && row.state === "stored" && row.file && (
          <ButtonTip label={t("onboardingPage.tips.removeFile")}>
<button
            type="button"
            onClick={() => onRemove(row.file!)}
            aria-label={t("onboardingPage.slot.removeFile", { name: row.name })}
            className="-mr-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-sm:h-11 max-sm:w-11"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
          </ButtonTip>
        )}
        {(row.state === "error" || (row.state === "waiting" && row.retrying)) && row.item && (
          <ButtonTip label={t("onboardingPage.tips.dismissUpload")}>
<button
            type="button"
            onClick={() => onDismiss(row.item!)}
            aria-label={row.state === "error" ? t("onboardingPage.slot.dismiss") : t("onboardingPage.slot.cancelRetry")}
            className="-mr-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground max-sm:h-11 max-sm:w-11"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
          </ButtonTip>
        )}
      </div>

      <Collapse open={busy}>
        <div className="flex items-center gap-3 pt-2">
          <div className="h-1 flex-1 overflow-hidden rounded-full bg-secondary" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}>
            <div
              className="h-full w-full origin-left rounded-full bg-primary"
              style={{ transform: `scaleX(${Math.max(row.progress, row.state === "waiting" ? 0 : 0.03)})`, transition: `transform 400ms ${EASE}` }}
            />
          </div>
          <span className="shrink-0 text-xs text-muted-foreground">
            {row.state === "waiting"
              ? row.retrying
                ? t("onboardingPage.slot.retrying")
                : t("onboardingPage.slot.waiting")
              : t("onboardingPage.slot.uploading", { percent })}
          </span>
        </div>
      </Collapse>

      <Collapse open={row.state === "error"}>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-1.5">
          <p role="alert" className="text-xs text-muted-foreground">
            {lastError.current}
          </p>
          {RETRYABLE.has(row.errorCode ?? "unknown") && row.item && (
            <ButtonTip label={t("onboardingPage.tips.retryUpload")}>
<button type="button" onClick={() => onRetry(row.item!)} className="text-xs font-medium underline underline-offset-2">
              {t("onboardingPage.slot.retry")}
            </button>
          </ButtonTip>
          )}
        </div>
      </Collapse>

      {/* the small look inside: local, from the first 64 KB, only ever text */}
      <CollapsePresence show={!!peek}>
        {peek && (
          <div className="space-y-1 pt-2 text-xs text-muted-foreground" data-testid="csv-peek">
            <p>
              {t("onboardingPage.peek.columns", { count: peek.columns.length, list: columnList })}{" "}
              ({t(peek.approximate ? "onboardingPage.peek.rowsApprox" : "onboardingPage.peek.rows", { count: peek.rows })})
            </p>
            {nudge?.noId && <p>{t("onboardingPage.peek.noId")}</p>}
            {nudge?.genericNames && <p>{t("onboardingPage.peek.generic")}</p>}
          </div>
        )}
      </CollapsePresence>
    </div>
  );
}

/**
 * The upload area of one data slot: a large drop zone (the whole page takes a drop while an upload
 * step is open; the zone only shows it), a real file input, and the files as cards that glide in
 * with a little spring, show progress, and fold away when removed. Everything that comes from an
 * upload - file names above all - is rendered as text.
 *
 * Rows keep their key from picking to storing, so a finished upload changes in place; a list present
 * at the first render does not animate.
 */
export default function SlotUploader({
  slot,
  title,
  description,
  stored,
  items,
  canEdit,
  notice,
  errorText,
  onPick,
  onRemove,
  onRetry,
  onDismiss,
  onDismissNotice,
  dragging = false,
  compact = false,
  hints = false,
  onSkipped,
  preview,
}: SlotUploaderProps) {
  const { t, i18n } = useTranslation();
  const reduced = usePrefersReducedMotion();
  const inputRef = useRef<HTMLInputElement>(null);
  const locale = i18n.language?.startsWith("en") ? "en-GB" : "de-DE";
  const extensionList = slot.extensions.map((extension) => extension.replace(/^\./, "").toUpperCase()).join(", ");
  const headingId = `onboarding-slot-${slot.slot}`;
  const rows = buildRows(stored, items);
  const [previewing, setPreviewing] = useState<PublicFileView | null>(null);
  // the button that opened the look: the focus goes back to it (Safari does not focus a button on click, so it is kept from the event)
  const opener = useRef<HTMLElement | null>(null);
  const fulfilled = slot.required && stored.length > 0;

  // a compact zone takes its own drops (the page-wide drop belongs to the single-slot steps)
  const [localDrag, setLocalDrag] = useState(0);
  const localHandlers = compact
    ? {
        onDragEnter: (event: React.DragEvent) => {
          if (!dragHasFiles(event.nativeEvent)) return;
          event.preventDefault();
          setLocalDrag((n) => n + 1);
        },
        onDragOver: (event: React.DragEvent) => {
          if (!dragHasFiles(event.nativeEvent)) return;
          event.preventDefault();
        },
        onDragLeave: (event: React.DragEvent) => {
          if (!dragHasFiles(event.nativeEvent)) return;
          setLocalDrag((n) => Math.max(0, n - 1));
        },
        onDrop: (event: React.DragEvent) => {
          if (!dragHasFiles(event.nativeEvent)) return;
          event.preventDefault();
          event.stopPropagation();
          setLocalDrag(0);
          void readDrop(event.dataTransfer, slot.extensions).then((dropped) => {
            if (dropped.files.length) onPick(dropped.files);
            if (dropped.skipped) onSkipped?.(dropped.skipped);
          });
        },
      }
    : {};
  const isDragging = dragging || localDrag > 0;
  const rowTransition = { duration: reduced ? 0.2 : 0.4, ease: EASE_ARRAY };
  const spring = reduced ? { duration: 0.2 } : { type: "spring" as const, stiffness: 320, damping: 26 };

  return (
    <section aria-labelledby={headingId} data-testid={`slot-${slot.slot}`} data-fulfilled={fulfilled ? "true" : "false"} className={cn(compact ? "rounded-2xl border border-border p-3" : "")}>
      {compact && (
        <div className="mb-2 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h3 id={headingId} className="text-sm font-semibold">
              {title}
            </h3>
            <p className="text-xs text-muted-foreground" title={description}>
              {t("onboardingPage.slot.extensions", { list: extensionList })}
            </p>
          </div>
          <span data-testid={`slot-count-${slot.slot}`} className="shrink-0 text-xs text-muted-foreground">
            {rows.length > 0 ? t("onboardingPage.slot.count", { count: stored.length }) : ""}
          </span>
        </div>
      )}
      {!compact && <h3 id={headingId} className="sr-only">{title}</h3>}

      {canEdit && (
        <motion.div
          data-dragging={isDragging ? "true" : "false"}
          animate={{ scale: isDragging && !reduced ? 1.015 : 1 }}
          transition={spring}
          className="relative"
          {...(localHandlers as Record<string, unknown>)}
        >
          <input
            ref={inputRef}
            type="file"
            multiple={slot.maxFiles !== 1}
            accept={slot.extensions.map((extension) => `.${extension.replace(/^\./, "")}`).join(",")}
            className="sr-only"
            tabIndex={-1}
            data-testid={`upload-${slot.slot}`}
            onChange={(event) => {
              const chosen = Array.from(event.target.files ?? []);
              event.target.value = "";
              if (chosen.length) onPick(chosen);
            }}
          />
          <ButtonTip label={t("onboardingPage.tips.chooseFiles")} side="bottom">
          <button
            type="button"
            aria-label={t("onboardingPage.slot.choose", { slot: title })}
            onClick={() => inputRef.current?.click()}
            className={cn(
              "group relative flex w-full flex-col items-center justify-center gap-1.5 overflow-hidden rounded-2xl text-center transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              compact ? "px-3 py-3" : rows.length > 0 ? "px-6 py-5" : "px-6 py-9",
              isDragging ? "bg-primary/5" : "bg-muted/30 hover:bg-muted/50",
            )}
          >
            {/* the frame: dashed at rest, a line that draws itself around the zone while a drag is over the page */}
            <svg className="pointer-events-none absolute inset-0 h-full w-full" aria-hidden="true">
              <rect x="1" y="1" width="calc(100% - 2px)" height="calc(100% - 2px)" rx="16" fill="none" strokeWidth="1.5" className={cn("stroke-border transition-opacity duration-300", isDragging ? "opacity-0" : "opacity-100")} strokeDasharray="5 6" />
              <rect x="1" y="1" width="calc(100% - 2px)" height="calc(100% - 2px)" rx="16" fill="none" strokeWidth="1.8" pathLength={1} className="stroke-primary" style={{ strokeDasharray: 1, strokeDashoffset: isDragging ? 0 : 1, transition: reduced ? "none" : `stroke-dashoffset 700ms ${EASE}` }} />
            </svg>
            <UploadCloud className={cn("transition-transform duration-300", compact || rows.length > 0 ? "h-5 w-5" : "h-8 w-8", "text-muted-foreground", isDragging && !reduced && "-translate-y-1")} aria-hidden="true" />
            <span className={cn("font-medium", compact ? "text-xs" : "text-base")}>
              {isDragging ? t("onboardingPage.slot.dropActive") : compact ? t("onboardingPage.slot.drop") : t("onboardingPage.slot.bigDrop")}
            </span>
            {!compact && rows.length === 0 && <span className="text-sm text-muted-foreground">{t("onboardingPage.slot.bigDropSub")}</span>}
            {!compact && <span className="text-xs text-muted-foreground">{t("onboardingPage.slot.extensions", { list: extensionList })}</span>}
          </button>
          </ButtonTip>
        </motion.div>
      )}

      <CollapsePresence show={!!notice}>
        <p role="alert" className="mt-3 flex items-start gap-2 text-sm text-muted-foreground">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="flex-1">{notice}</span>
          {onDismissNotice && (
            <ButtonTip label={t("onboardingPage.tips.dismissNotice")}>
              <button type="button" onClick={onDismissNotice} aria-label={t("onboardingPage.slot.dismiss")}>
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </ButtonTip>
          )}
        </p>
      </CollapsePresence>

      <ul className="-mx-1 mt-3 max-h-[26rem] overflow-y-auto px-1 empty:mt-0" data-testid={`files-${slot.slot}`}>
        <AnimatePresence initial={false}>
          {rows.map((row) => (
            <motion.li
              key={row.key}
              data-row-key={row.key}
              style={{ display: "grid" }}
              initial={{ opacity: 0, scale: reduced ? 1 : 0.94, y: reduced ? 0 : -10, gridTemplateRows: "0fr" }}
              animate={{ opacity: 1, scale: 1, y: 0, gridTemplateRows: "1fr" }}
              exit={{ opacity: 0, scale: reduced ? 1 : 0.96, gridTemplateRows: "0fr" }}
              transition={{ ...rowTransition, scale: spring, y: spring }}
            >
              <div className="min-h-0 overflow-hidden">
                <div className="pb-2">
                  <FileCard row={row} canEdit={canEdit} slot={slot} locale={locale} hints={hints} errorText={errorText} onRemove={onRemove} onRetry={onRetry} onDismiss={onDismiss} onPreview={preview ? (file, button) => { opener.current = button; setPreviewing(file); } : undefined} />
                </div>
              </div>
            </motion.li>
          ))}
        </AnimatePresence>
      </ul>
      {preview && previewing && <FilePreviewDialog key={previewing.id} name={previewing.originalName} fileId={previewing.id} api={preview} onClose={() => { setPreviewing(null); window.setTimeout(() => opener.current?.focus(), 0); }} />}
    </section>
  );
}
